import { makeAutoObservable } from "mobx";
import RootStore from "./RootStore";

/**
 * Backward-compatible facade.
 *
 * Старые компоненты пока используют `const { user } = useContext(Context)`.
 * Внутри состояние уже разделено на независимые store:
 * auth, profile, server, chat, log, player.
 *
 * Это позволяет постепенно переводить компоненты на новые store без большого взрывного рефакторинга.
 */
export default class UserStore {
    constructor() {
        this.root = new RootStore();

        this.authStore = this.root.auth;
        this.profileStore = this.root.profile;
        this.serverStore = this.root.server;
        this.chatStore = this.root.chat;
        this.logStore = this.root.log;
        this.playerStore = this.root.player;

        makeAutoObservable(this, {
            root: false,
            authStore: false,
            profileStore: false,
            serverStore: false,
            chatStore: false,
            logStore: false,
            playerStore: false,
        }, { autoBind: true });
    }

    checkAuth() {
        // Авторизация теперь инициализируется в App.js через authService.
        // Метод оставлен для обратной совместимости со старыми компонентами.
        this.setIsLoading(false);
    }

    reset() {
        this.root.resetAll();
    }

    setIsAuth(value) {
        this.authStore.setIsAuth(value);
    }

    setIsLoading(value) {
        this.authStore.setIsLoading(value);
    }

    setUser(user) {
        this.authStore.setUser(user);
    }

    setUserName(userName) {
        this.authStore.setUserName(userName);
    }

    setRole(role) {
        this.authStore.setRole(role);
    }

    setUserInfo(userInfo) {
        this.profileStore.setUserInfo(userInfo);
    }

    setUsers(users) {
        this.profileStore.setUsers(users);
    }

    setServers(servers) {
        this.serverStore.setServers(servers);
    }

    setChat(storage) {
        this.chatStore.setChat(storage);
    }

    setLog(storage) {
        this.logStore.setLog(storage);
    }

    replaceLog(storage) {
        this.logStore.replaceLog(storage);
    }

    clearLog(logId) {
        this.logStore.clearLog(logId);
    }

    pauseLog(logId) {
        this.logStore.pauseLog(logId);
    }

    resumeLog(logId) {
        this.logStore.resumeLog(logId);
    }

    isLogPaused(logId) {
        return this.logStore.isPaused(logId);
    }

    setPlayerInfo(storage) {
        this.playerStore.setPlayerInfo(storage);
    }

    getStorageByChatId(chatId) {
        return this.chatStore.getStorageByChatId(chatId);
    }

    getStorageByLogId(logId) {
        return this.logStore.getStorageByLogId(logId);
    }

    getStorageByPlayerInfoId(playerInfoId) {
        return this.playerStore.getStorageByPlayerInfoId(playerInfoId);
    }

    get user() {
        return this.authStore.user;
    }

    get userName() {
        return this.authStore.userName;
    }

    get role() {
        return this.authStore.role;
    }

    get isAuth() {
        return this.authStore.isAuth;
    }

    get isLoading() {
        return this.authStore.isLoading;
    }

    get userInfo() {
        return this.profileStore.userInfo;
    }

    get users() {
        return this.profileStore.users;
    }

    get servers() {
        return this.serverStore.servers;
    }

    get chat() {
        return this.chatStore.chat;
    }

    get log() {
        return this.logStore.log;
    }

    get player_info() {
        return this.playerStore.player_info;
    }

    get player_count() {
        return this.playerStore.player_count;
    }
}
